import React, { useState } from "react";
import PropTypes from "prop-types";

import Button from "ui/Button";

import { Alert, AlertTitle, Checkbox, FormControlLabel } from "@mui/material";
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import UndoIcon from "@mui/icons-material/Undo";
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';

// Collect / uncollect actions for a merch instance — the non-ticket counterpart
// of the ticket check-in Actions, with a group-collect option for multi-item
// orders. Merch is never pending, so there is no approve path.
const Actions = ({ uuid, redeemState, orderAmount, orderItems, onClick }) => {
  const [collectAll, setCollectAll] = useState(false);

  const items = orderItems || [];
  const showGroupAlert = redeemState === "initiated" && orderAmount > 1;

  const buttonTypes = {
    collect: {
      label: "Collect",
      icon: CheckCircleOutlineIcon,
      action: () => onClick(uuid, false, "collect"),
    },
    uncollect: {
      label: "Mark as Not Collected",
      icon: UndoIcon,
      action: () => onClick(uuid, false, "uncollect"),
    },
    collectGroup: {
      label: `Collect ${orderAmount} remaining`,
      icon: GroupIcon,
      action: () => onClick(uuid, false, "collect", orderAmount),
    },
    collectIndividual: {
      label: "Collect 1 Item",
      icon: PersonIcon,
      action: () => onClick(uuid, false, "collect"),
    },
  };

  const getButtonConfig = () => {
    if (redeemState === "completed") return buttonTypes.uncollect;
    if (orderAmount > 1 && redeemState === "initiated") {
      return collectAll ? buttonTypes.collectGroup : buttonTypes.collectIndividual;
    }
    return buttonTypes.collect;
  };

  const config = getButtonConfig();

  return (
    <div style={{width: '100%', display: 'flex', flexDirection: 'column', gap: '8px'}}>
      {showGroupAlert && (
        <Alert
          severity="warning"
          variant="outlined"
          icon={<Inventory2OutlinedIcon />}
          sx={{ '& .MuiAlert-icon': { order: 1, ml: 'auto', mr: 0 }}}
        >
          <AlertTitle sx={{ fontWeight: 600, color: 'black', mb: 1 }}>This order includes {items.length} items — confirm with the buyer:</AlertTitle>
          <div style={{ marginBottom: '12px' }}>
            {items.map((oi) => (
              <div key={oi.uuid} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'black', padding: '2px 0' }}>
                {oi.redeem_state === 'completed'
                  ? <CheckCircleOutlineIcon fontSize="small" color="success" />
                  : <CancelOutlinedIcon fontSize="small" color="warning" />}
                <span style={{ fontSize: '0.95rem' }}>{oi.title}{oi.variant_label ? ` — ${oi.variant_label}` : ''}{oi.uuid === uuid ? ' (scanned)' : ''}</span>
              </div>
            ))}
          </div>
          <FormControlLabel
            sx={{ '& .MuiFormControlLabel-label': { fontSize: '1rem', color: 'black' }}}
            control={
              <Checkbox
                sx={{ '& .MuiSvgIcon-root': { fontSize: '1.2rem' }}}
                color="warning"
                checked={collectAll}
                onChange={(e) => setCollectAll(e.target.checked)}
              />
            }
            label={`Collect all items in this order`}
          />
        </Alert>
      )}

      <Button highlighted icon={config.icon} onClick={config.action} fullwidth>
        {config.label}
      </Button>
    </div>
  );
};

Actions.propTypes = {
  uuid: PropTypes.string,
  redeemState: PropTypes.string,
  orderAmount: PropTypes.number,
  orderItems: PropTypes.array,
  onClick: PropTypes.func,
};

export default Actions;
